import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { emailApi } from "../../services/api";

// ============================================================================
// Support Email Thunks (New API endpoints: /api/email/*)
// ============================================================================

export const getMailDetails = createAsyncThunk("mailbox/getMailDetails", async (folder?: string) => {
  const targetFolder = folder || "inbox";
  try {
    const endpoint = targetFolder === "all" ? "all" : targetFolder;
    const data = await emailApi.getEmails(endpoint);

    // Transform to match frontend expected format
    return data.emails.map((email: any, index: number) => ({
      id: email.id,
      forId: `email-${email.id}`,
      name: email.from_name || email.from_email.split('@')[0],
      number: "",
      subject: email.subject,
      teaser: email.body_text?.substring(0, 100) || "",
      date: formatEmailDate(email.received_at || email.sent_at || email.created_at),
      type: "primary",
      category: email.folder,
      label: email.labels?.[0] || "",
      img: null, // No avatar for external emails
      badge: email.labels?.[0] || null,
      badgeClass: getBadgeClass(email.labels?.[0]),
      unread: !email.is_read,
      starred: email.is_starred,
      // Additional fields
      from_email: email.from_email,
      from_name: email.from_name,
      to_email: email.to_email,
      body_text: email.body_text,
      body_html: email.body_html,
      attachments: email.attachments || [],
      message_id: email.message_id,
    }));
  } catch (error) {
    console.error("Error fetching mails:", error);
    return [];
  }
});

export const getMailById = createAsyncThunk("mailbox/getMailById", async (emailId: number) => {
  try {
    const email = await emailApi.getEmailById(emailId);
    return email;
  } catch (error) {
    console.error("Error fetching mail:", error);
    return null;
  }
});

export const unreadMail = createAsyncThunk("mailbox/unreadMail", async (mail: any) => {
  try {
    const emailId = typeof mail === 'object' ? (mail.id || mail.forId?.replace('email-', '')) : mail;
    await emailApi.updateEmail(emailId, { is_read: false });

    return emailId;
  } catch (error) {
    console.error("Error marking mail as unread:", error);
    throw error;
  }
});

export const staredMail = createAsyncThunk("mailbox/staredMail", async (mail: any) => {
  try {
    const emailId = typeof mail === 'object' ? (mail.id || mail.forId?.replace('email-', '')) : mail;
    const mailObj = typeof mail === 'object' ? mail : null;
    const isStarred = mailObj ? !mailObj.starred : true;

    await emailApi.updateEmail(emailId, { is_starred: isStarred });

    return emailId;
  } catch (error) {
    console.error("Error starring mail:", error);
    throw error;
  }
});

export const trashMail = createAsyncThunk("mailbox/trashMail", async (mail: any) => {
  try {
    const emailId = typeof mail === 'object' ? (mail.id || mail.forId?.replace('email-', '')) : mail;

    await emailApi.trashEmail(emailId);

    toast.success("Mail moved to trash", { autoClose: 3000 });
    return emailId;
  } catch (error) {
    toast.error("Failed to move mail to trash", { autoClose: 3000 });
    throw error;
  }
});

export const deleteMail = createAsyncThunk("mailbox/deleteMail", async (mail: any) => {
  try {
    const emailId = typeof mail === 'object' ? (mail.id || mail.forId?.replace('email-', '')) : mail;

    await emailApi.deleteEmail(emailId);

    toast.success("Mail deleted permanently", { autoClose: 3000 });
    return emailId;
  } catch (error) {
    toast.error("Failed to delete mail", { autoClose: 3000 });
    throw error;
  }
});

export const labelMail = createAsyncThunk("mailbox/labelMail", async (mail: any) => {
  try {
    const emailId = mail.id || mail.forId?.replace('email-', '');
    const labels = [mail.e || mail.label];

    await emailApi.updateEmail(emailId, { labels });

    return { response: mail.forId, label: mail.e || mail.label };
  } catch (error) {
    console.error("Error labeling mail:", error);
    throw error;
  }
});

export const sendMail = createAsyncThunk("mailbox/sendMail", async (mailData: {
  to_email: string;
  subject: string;
  body_html: string;
  body_text?: string;
  cc?: string[];
  bcc?: string[];
}) => {
  try {
    const data = await emailApi.sendEmail(mailData);
    toast.success("Email sent successfully", { autoClose: 3000 });
    return data;
  } catch (error: any) {
    toast.error(error.message || "Failed to send email", { autoClose: 3000 });
    throw error;
  }
});

export const replyMail = createAsyncThunk("mailbox/replyMail", async ({ emailId, body_html, body_text }: {
  emailId: number;
  body_html: string;
  body_text?: string;
}) => {
  try {
    const data = await emailApi.replyEmail(emailId, { body_html, body_text });
    toast.success("Reply sent successfully", { autoClose: 3000 });
    return data;
  } catch (error: any) {
    toast.error(error.message || "Failed to send reply", { autoClose: 3000 });
    throw error;
  }
});

export const getEmailStats = createAsyncThunk("mailbox/getEmailStats", async () => {
  try {
    return await emailApi.getStats();
  } catch (error) {
    console.error("Error fetching email stats:", error);
    return null;
  }
});

// ============================================================================
// Helper Functions
// ============================================================================

function formatEmailDate(dateStr: string | null): string {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Today - show time
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } else if (diffDays < 7) {
    // This week - show day name
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else if (date.getFullYear() === now.getFullYear()) {
    // This year - show month and day
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else {
    // Different year - show full date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

function getBadgeClass(label: string | null): string {
  if (!label) return "";

  const badgeClasses: Record<string, string> = {
    support: "info",
    freelance: "primary",
    social: "warning",
    friend: "success",
    family: "danger",
  };

  return badgeClasses[label.toLowerCase()] || "secondary";
}
